
import axios from 'axios';
import { debug, error, log } from '@/utils/logger';
import { isTokenExpired } from '@/utils/tokenUtils';

// Create axios instance for booking API calls
// Booking endpoints require authentication (Bearer token)
const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_APP_API_BASE_URL || 'https://app.globethrivers.com',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set up authentication interceptors for booking API calls
// All /api/v1/booking/* endpoints require authentication
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    const idToken = localStorage.getItem('idToken');

    // Check token expiration before making request
    if (token && isTokenExpired(token)) {
      log('🔐 [nuiteeBookingService] Access token expired', {
        url: config.url,
      });
      return Promise.reject(new Error('Token expired'));
    }

    if (idToken && isTokenExpired(idToken)) {
      log('🔐 [nuiteeBookingService] ID token expired', {
        url: config.url,
      });
      return Promise.reject(new Error('Token expired'));
    }

    // Add auth headers (matching /api/v1/* pattern)
    if (token) {
      config.headers.Authorization = token;
    }

    if (idToken) {
      config.headers['x-id-token'] = idToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401/403 auth errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      log('🔐 [nuiteeBookingService] Authentication error', {
        status: error.response?.status,
        url: error.config?.url,
      });
    }
    return Promise.reject(error);
  }
);

/** Extended Error with booking-specific metadata attached after creation. */
interface BookingEnhancedError extends Error {
  code?: string;
  status?: number;
  responseData?: unknown;
}

/** Shape of an axios-like error response used in catch blocks. */
interface AxiosLikeError {
  response?: {
    status?: number;
    statusText?: string;
    data?: {
      error?: string;
      message?: string;
      code?: string;
    };
  };
  config?: { url?: string };
  message?: string;
  stack?: string;
  constructor?: { name?: string };
}

/** Session-level cache for hotel Place IDs fetched on demand. Avoids re-fetching the same hotel. */
const placeIdCache = new Map<string, { google_place_id: string | null; hotel_google_place_id: string | null }>();

export interface HotelPlaceIdsResponse {
  nuitee_hotel_id: string;
  google_place_id: string | null;
  hotel_google_place_id: string | null;
}

export interface HotelSearchParams {
  placeId?: string;
  latitude?: number;
  longitude?: number;
  hotelIds?: string[]; // For exact match search - takes priority over placeId/coordinates
  checkIn: string; // YYYY-MM-DD (frontend format)
  checkOut: string; // YYYY-MM-DD (frontend format)
  adults: number;
  rooms: number;
}

// Response from hotel ID lookup endpoint
export interface HotelIdLookupResponse {
  status: string;
  data: {
    hotelId: string | null;
    hotelName: string | null;
    exactMatch: boolean;
    placeId: string;
    address?: string;
    rating?: number;
    starRating?: number;
    main_photo?: string;
    location?: { latitude: number; longitude: number };
    error?: string; // 'HOTEL_NOT_IN_INVENTORY' if not found
    message?: string;
  };
}

// Semantic search (GET /api/v1/booking/semantic-search) – discovery only; backend enriches with lat/long from hotel metadata
// Backend may return id or hotelId/hotel_id; frontend uses first present for dedupe and Book flow.
export interface SemanticSearchHotel {
  id?: string;
  hotelId?: string;
  hotel_id?: string;
  name: string;
  main_photo?: string;
  address: string;
  city?: string;
  country?: string;
  tags?: string[];
  score?: number;
  persona?: string;
  style?: string;
  location_type?: string;
  story?: string;
  latitude?: number;
  longitude?: number;
  google_place_id?: string;
  hotel_google_place_id?: string;
}

export interface SemanticSearchResponse {
  data: SemanticSearchHotel[];
}

export interface SemanticSearchParams {
  query: string;
  limit?: number; // 1–50, default 25
  min_rating?: number; // 0–5, default 0
}

// Backend request format (what we send to the API)
interface BackendSearchRequest {
  placeId?: string;
  latitude?: number;
  longitude?: number;
  hotelIds?: string[]; // For exact match search - takes priority over placeId/coordinates
  checkin: string; // lowercase
  checkout: string; // lowercase
  occupancies: Array<{
    adults: number;
    children?: number[]; // optional array of child ages
  }>;
  maxRatesPerHotel?: number; // Limits how many rate options are returned per hotel (default: 1)
}

// Backend response format (raw Nuitee response)
// The response is nested: response.data.data contains both hotels and data arrays
interface BackendSearchResponse {
  status: string;
  pricing?: HotelSearchPricing;
  data: {
    sandbox?: boolean;
    hotels?: Array<{
      id: string;
      name: string;
      address: string;
      rating?: number;
      main_photo?: string;
      latitude?: number;
      longitude?: number;
      google_place_id?: string;
      city?: string;
      country?: string;
    }>;
    data: Array<{
      hotelId: string;
      roomTypes: Array<{
        offerId: string; // Use this for prebook
        roomTypeId?: string;
        photos?: Array<{ url: string }>;
        rates: Array<{
          rateId?: string;
          name: string;
          mappedRoomId: number;
          boardName: string;
          boardType?: string;
          retailRate: {
            total: Array<{
              amount: number;
              currency: string;
            }>;
            suggestedSellingPrice?: Array<{
              amount: number;
              currency: string;
            }>;
            initialPrice?: Array<{
              amount: number;
              currency: string;
            }>;
            taxesAndFees?: Array<{
              amount: number;
              currency: string;
              included?: boolean;
            }>;
          };
          cancellationPolicies?: {
            refundableTag?: string; // "RFN" means refundable
            cancelPolicyInfos?: Array<{ cancelTime: string; amount: number; currency: string }>;
          };
          paymentTypes?: string[];
          perks?: Array<{ perkId: string; name: string }>;
          adultCount?: number;
          childCount?: number;
        }>;
      }>;
    }>;
  };
}

export interface HotelSearchPricing {
  margin_applied: boolean;
  pricing_tier: 'custom' | 'gt_plus' | 'platform_default' | 'standard';
  margin_percent: number | null;
  message: string | null;
}

export interface HotelSearchResponse {
  data: {
    hotels: Hotel[];
  };
  pricing?: HotelSearchPricing;
}

export interface Hotel {
  hotelId: string;
  name: string;
  address: string;
  city?: string;
  country?: string;
  rating?: number;
  images?: string[];
  latitude?: number;
  longitude?: number;
  google_place_id?: string;
  hotel_google_place_id?: string;
  rooms: HotelRoom[]; // Grouped by room with all rate options
  rates?: HotelRate[]; // Deprecated: use rooms instead
}

export interface HotelRoom {
  mappedRoomId: number;
  roomName: string;
  roomTypeId?: string;
  offerId: string; // Use this for prebook (from roomType level)
  rates: HotelRate[]; // All rate options for this room
  roomImages?: string[];
}

export interface HotelRate {
  rateId: string;
  offerId: string; // Use this for prebook when user selects this rate (correct offer per refundable vs non-refundable)
  mappedRoomId: number;
  roomName: string;
  boardName: string;
  boardType?: string;
  price: number; // Discounted/actual amount to charge (from retailRate.total) — use for main display price
  displayPrice: number; // Original list price before discount (from retailRate.suggestedSellingPrice) — use for strikethrough
  initialPrice?: number; // Legacy field from API (deprecated for discount calculation)
  currency: string;
  roomImages?: string[];
  cancellationPolicy?: string;
  isRefundable?: boolean;
  cancelPolicyInfos?: Array<{ cancelTime: string; amount: number; currency: string }>;
  breakfastIncluded?: boolean;
  taxesAndFees?: Array<{ amount: number; currency: string; included?: boolean }>;
  paymentTypes?: string[];
  perks?: Array<{ perkId: string; name: string }>;
  adultCount?: number;
  childCount?: number;
}

export interface PrebookParams {
  offerId: string; // Required - Use offerId from roomTypes[].offerId (NOT rateId)
  // Dates, adults, rooms are NOT needed - already encoded in the offer
}

export interface PrebookResponse {
  status: string;
  sandbox?: boolean; // Root-level field: false = live mode, true/undefined = sandbox
  data: {
    prebookId: string; // Use for booking completion
    transactionId: string; // Use for payment SDK and booking completion
    secretKey: string; // Use for payment SDK configuration
    offerId: string; // Echo of the offerId used
    hotelId: string; // Hotel ID
    price: number; // Total price
    currency: string; // Currency code (USD, EUR, etc.)
    paymentTypes?: string[]; // Array of payment types (e.g., ["NUITEE_PAY"])
    roomTypes?: Array<{
      rates: Array<{
        rateId: string;
        retailRate: { total: Array<{ amount: number; currency: string }> };
        cancellationPolicies: { refundableTag: 'RFN' | 'NRFN' };
      }>;
    }>;
  };
}

export interface CompleteBookingParams {
  prebookId: string; // From prebook response (response.data.prebookId)
  transactionId: string; // From prebook response (response.data.transactionId)
  holder: {
    firstName: string; // Required
    lastName: string; // Required
    email: string; // Required
  };
  guests: Array<{
    occupancyNumber: number; // Required - Room number (1, 2, 3, etc.)
    firstName: string; // Required
    lastName: string; // Required
    email: string; // Required
  }>;
  postId?: string; // Optional - for attribution (24-char hex MongoDB ObjectId)
  hotelId?: string; // Optional - Nuitee hotel ID for clientReference
  googlePlaceId?: string; // Optional - Google Place ID for clientReference
  locationId?: string; // Optional - canonical locations._id for booking_location_id resolution
  postLocationId?: string; // Optional - PostLocation slot id when booking from a trip/list place
  referralCreatorId?: string;
}

export interface CompleteBookingResponse {
  status: string;
  data: {
    bookingId: string; // Nuitee booking ID
    status: string; // "CONFIRMED" or other status
    hotelConfirmationCode: string; // Hotel's confirmation code
    checkin: string; // YYYY-MM-DD
    checkout: string; // YYYY-MM-DD
    hotel: {
      hotelId: string;
      name: string;
    };
    price: number; // Total price
    currency: string; // Currency code (USD, EUR, etc.)
    cancellationPolicies?: {
      refundableTag: 'RFN' | 'NRFN';
      cancelPolicyInfos?: Array<{
        cancelTime: string;
        amount: number;
        currency: string;
      }>;
    };
  };
  duplicate?: boolean; // Optional flag when backend detects duplicate transaction
  message?: string; // Optional message from backend
}

// Environment check response
export interface EnvironmentResponse {
  status: string;
  data: {
    isSandbox: boolean;
    environment: 'sandbox' | 'production';
  };
}

// Booking domain is not hardcoded: we send a path to POST /api/v1/booking/sso-link and use
// the full loginUrl/redirectUrl returned by the backend, so domain changes are backend-only.

export interface WhitelabelDeepLinkParams {
  hotelId: string;
  checkIn?: string; // YYYY-MM-DD
  checkOut?: string; // YYYY-MM-DD
  adults?: number;
  rooms?: number;
  children?: number[]; // Array of child ages
  postId?: string; // Optional post ID for attribution (24-char hex MongoDB ObjectId)
  googlePlaceId?: string; // Google Place ID for place traceability
}

/** Params for Hotel Listing Page deep link (/hotels) — no hotelId, for "see all hotels" CTA */
export interface WhitelabelListingDeepLinkParams {
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD
  placeId?: string; // Google Place ID for destination — scopes listing to city/area
  adults?: number;
  rooms?: number;
  children?: number[]; // Array of child ages
  postId?: string; // Optional for attribution
}

// SSO response from backend
export interface SSOResponse {
  status: 'success';
  data: {
    loginUrl: string;      // Load this in iframe
    expiresIn: number;     // Seconds (usually 1800 = 30 min)
    redirectUrl: string;  // Full URL after login
  };
}

// SSO request to backend
export interface SSORequest {
  redirectUrl: string; // Deep link path like "/hotels/{hotelId}?checkin=..."
}

// Hotel details response (enhanced)
export interface HotelDetailsResponse {
  status: 'success';
  data: {
    id: string;
    name: string;
    address: string;
    city?: string;
    country?: string;
    rating?: number;
    google_place_id?: string;
    hotel_google_place_id?: string;
    starRating?: number;
    reviewCount?: number;
    main_photo?: string;
    hotelDescription?: string;
    hotelImportantInformation?: string; // Resort fee info, important notices
    hotelImages?: Array<{ url: string; defaultImage?: boolean }>;
    hotelFacilities?: string[];
    checkinCheckoutTimes?: {
      checkin?: string;
      checkout?: string;
      checkinStart?: string;
      checkinEnd?: string;
    };
    policies?: Array<{
      policy_type?: string;
      type?: string;
      name?: string;
      text?: string;
      description?: string;
      child_allowed?: string;
      pets_allowed?: string;
      parking?: string;
    }>;
    location?: {
      latitude: number;
      longitude: number;
    };
    sentiment_analysis?: {
      pros?: string[];
      cons?: string[];
    };
    rooms?: Array<{
      id: number;
      roomName: string;
      photos?: Array<{ url: string }>;
      roomAmenities?: Array<{ id?: string; name?: string; [key: string]: unknown }>;
      roomSizeSquare?: number;
      roomSizeUnit?: string;
      maxOccupancy?: number;
      description?: string;
      bedTypes?: Array<{ id?: string; name?: string; count?: number; [key: string]: unknown }>;
    }>;
  };
}

// Hotel reviews response
export interface HotelReviewsResponse {
  status: 'success';
  data: {
    summary: {
      overallScore: number | null;
      reviewCount: number;
      sentiment?: {
        pros: string[];
        cons: string[];
      } | null;
    };
    reviews: Array<{
      id: string;
      reviewerName: string;
      date: string;
      rating: number | null;
      text: string;
      travelerType?: string | null;
      title?: string | null;
      positiveText?: string | null;
      negativeText?: string | null;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  };
}

// Facilities response
export interface FacilitiesResponse {
  status: 'success';
  data: {
    facilities: Array<{
      id: string;
      name: string;
      category?: string;
    }>;
  };
}

// Cancellation policy info structure
export interface CancellationPolicyInfo {
  cancelTime: string;
  amount: number;
  currency: string;
}

export interface CancellationPolicies {
  refundableTag: 'RFN' | 'NRFN';
  cancelPolicyInfos?: CancellationPolicyInfo[];
}

// Booking management interfaces
export interface BookingListItem {
  bookingId: string;
  hotelConfirmationCode: string | null;
  status: 'Ready' | 'Pending' | 'Cancelled';
  hotel: {
    hotelId: string | null;
    name: string | null;
  };
  checkin: string;
  checkout: string;
  price: number;
  currency: string;
  holder: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
  numberOfGuests: number | null;
  numberOfRooms: number | null;
  createdAt: string;
  attribution: {
    postId: string;
    postType: string;
  } | null;
  // Policy fields (included in list response from backend)
  isRefundable?: boolean | null; // null = unknown, true = refundable, false = non-refundable
  cancellationPolicies?: CancellationPolicies | null;
  refundableTag?: 'RFN' | 'NRFN' | null; // Convenience field
}

export interface BookingsListResponse {
  status: 'success';
  data: {
    bookings: BookingListItem[];
    pagination: {
      page: number;
      limit: number;
      totalCount: number;
      totalPages: number;
      hasNext: boolean;
      hasPrevious: boolean;
    };
  };
}

export interface CancelBookingResponse {
  status: 'success';
  data: {
    bookingId: string;
    status: 'Cancelled';
    cancelledAt: string;
    cancellationReason: string | null;
  };
}

export interface AmendBookingResponse {
  status: 'success';
  data: {
    bookingId: string;
    amendedAt: string;
    guests: Array<{
      firstName: string;
      lastName: string;
      occupancyNumber: number;
    }>;
  };
}

export type BookingErrorCode =
  | 'USER_NOT_FOUND'
  | 'BOOKING_ACCESS_DENIED'
  | 'BOOKING_NOT_FOUND'
  | 'BOOKING_ALREADY_CANCELLED'
  | 'BOOKING_CANCELLED'
  | 'CANCELLATION_NOT_ALLOWED'
  | 'AMENDMENT_NOT_ALLOWED';

export interface BookingErrorResponse {
  status: 'error';
  error: string;
  message?: string;
  code: BookingErrorCode;
}

export const nuiteeBookingService = {
  /**
   * Build a deep link path (relative path, not full URL) for SSO redirect
   * This is used as the redirectUrl parameter for the SSO endpoint
   * 
   * @param params - Hotel ID and optional search parameters
   * @returns Deep link path like "/hotels/{hotelId}?checkin=...&clientReference=..."
   */
  buildWhitelabelDeepLinkPath: (params: WhitelabelDeepLinkParams): string => {
    const { hotelId, checkIn, checkOut, adults = 2, rooms = 1, children = [], postId, googlePlaceId } = params;
    
    log('[nuiteeBookingService] Building whitelabel deep link path for SSO - input params', {
      hotelId,
      checkIn,
      checkOut,
      adults,
      rooms,
      childrenCount: children.length,
      postId: postId || null,
      hasPostId: !!postId,
      googlePlaceId: googlePlaceId || null,
    });
    
    // Build path (not full URL)
    let path = `/hotels/${hotelId}`;
    
    // Build query params
    const queryParams: string[] = [];
    
    if (checkIn) {
      queryParams.push(`checkin=${checkIn}`);
    }
    
    if (checkOut) {
      queryParams.push(`checkout=${checkOut}`);
    }
    
    if (checkIn && checkOut) {
      const occupancies = Array(rooms).fill(null).map(() => ({
        adults,
        children,
      }));
      const occupanciesBase64 = btoa(JSON.stringify(occupancies));
      queryParams.push(`occupancies=${occupanciesBase64}`);
      
      log('[nuiteeBookingService] Built occupancies for SSO deep link path', {
        occupancies,
        occupanciesBase64,
        occupanciesLength: occupanciesBase64.length,
      });
    }
    
    // Build clientReference with format: gt:{postId|NoPost}:hotel:{hotelId}:place:{googlePlaceId}:ts:{timestamp}
    // Full traceability: postId for attribution, hotelId, googlePlaceId for place, timestamp for uniqueness
    const isValidPostId = postId && typeof postId === 'string' && /^[0-9a-fA-F]{24}$/.test(postId);
    const postPart = isValidPostId ? postId : 'NoPost';
    const placePart = googlePlaceId || 'NoPlace';
    const timestamp = Date.now();
    const clientReference = `gt:${postPart}:hotel:${hotelId}:place:${placePart}:ts:${timestamp}`;
    queryParams.push(`clientReference=${encodeURIComponent(clientReference)}`);
    
    log('[nuiteeBookingService] Added clientReference to deep link', {
      postId: postId || null,
      isValidPostId,
      googlePlaceId: googlePlaceId || null,
      hotelId,
      timestamp,
      clientReference,
    });
    
    // Append query string if any params exist
    if (queryParams.length > 0) {
      path += '?' + queryParams.join('&');
    }
    
    log('[nuiteeBookingService] Built whitelabel deep link path for SSO', { 
      hotelId, 
      checkIn, 
      checkOut, 
      adults, 
      rooms,
      childrenCount: children.length,
      postId: postId || null,
      clientReference,
      hasQueryParams: queryParams.length > 0,
      queryParamsCount: queryParams.length,
      pathLength: path.length,
      path,
    });
    
    return path;
  },

  /**
   * Build deep link path for Hotel Listing Page (/hotels) — no hotelId.
   * Used for "see all hotels" CTA (e.g. Results Hotels tab). Same SSO flow as buildWhitelabelDeepLinkPath.
   *
   * @param params - checkIn, checkOut, optional adults/rooms/children/postId
   * @returns Path like "/hotels?checkin=...&checkout=...&occupancies=...&clientReference=..."
   */
  buildWhitelabelListingDeepLinkPath: (params: WhitelabelListingDeepLinkParams): string => {
    const { checkIn, checkOut, placeId, adults = 2, rooms = 1, children = [], postId } = params;

    log('[nuiteeBookingService] Building whitelabel listing deep link path for SSO - input params', {
      checkIn,
      checkOut,
      placeId: placeId || null,
      adults,
      rooms,
      childrenCount: children.length,
      postId: postId || null,
    });

    let path = '/hotels';
    const queryParams: string[] = [];

    if (placeId) {
      queryParams.push(`placeId=${encodeURIComponent(placeId)}`);
    }

    queryParams.push(`checkin=${checkIn}`);
    queryParams.push(`checkout=${checkOut}`);

    const occupancies = Array(rooms).fill(null).map(() => ({
      adults,
      children,
    }));
    const occupanciesBase64 = btoa(JSON.stringify(occupancies));
    queryParams.push(`occupancies=${occupanciesBase64}`);

    const isValidPostId = postId && typeof postId === 'string' && /^[0-9a-fA-F]{24}$/.test(postId);
    const postPart = isValidPostId ? postId : 'NoPost';
    const placePart = placeId || 'NoPlace';
    const timestamp = Date.now();
    const clientReference = `gt:${postPart}:listing:place:${placePart}:ts:${timestamp}`;
    queryParams.push(`clientReference=${encodeURIComponent(clientReference)}`);

    path += '?' + queryParams.join('&');

    log('[nuiteeBookingService] Built whitelabel listing deep link path for SSO', {
      placeId: placeId || null,
      pathLength: path.length,
      path,
    });

    return path;
  },

  /**
   * Get SSO login URL from backend
   * 
   * @param redirectUrl - Deep link path (e.g., "/hotels/{hotelId}?checkin=...")
   * @returns SSO response with loginUrl to load in iframe
   */
  getSSOLink: async (redirectUrl: string): Promise<SSOResponse> => {
    const endpoint = '/api/v1/booking/sso-link';
    
    try {
      log('[nuiteeBookingService] POST /api/v1/booking/sso-link - Request', {
        endpoint,
        redirectUrl,
      });

      const response = await apiClient.post<SSOResponse>(
        endpoint,
        { redirectUrl },
        { timeout: 10000 } // 10 seconds timeout
      );

      log('[nuiteeBookingService] POST /api/v1/booking/sso-link - Response', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        responseData: {
          hasLoginUrl: !!response.data.data?.loginUrl,
          loginUrlLength: response.data.data?.loginUrl?.length || 0,
          expiresIn: response.data.data?.expiresIn,
          redirectUrl: response.data.data?.redirectUrl,
        },
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      const status = axiosErr?.response?.status;
      const errorCode = axiosErr?.response?.data?.code;
      const errorMessage = axiosErr?.response?.data?.error || axiosErr?.message;
      
      log('[nuiteeBookingService] POST /api/v1/booking/sso-link - Error', {
        endpoint,
        error: {
          message: errorMessage,
          status,
          statusText: axiosErr?.response?.statusText,
          code: errorCode,
          responseData: axiosErr?.response?.data,
        },
      });
      
      // Handle specific error codes
      if (status === 400) {
        if (errorCode === 'MISSING_REDIRECT_URL') {
          error('[nuiteeBookingService] Missing redirectUrl in SSO request', { code: errorCode, message: errorMessage });
        } else {
          error('[nuiteeBookingService] Bad request for SSO', { code: errorCode, message: errorMessage });
        }
      } else if (status === 401) {
        if (errorCode === 'USER_NOT_FOUND') {
          error('[nuiteeBookingService] User not found - must be logged in', { code: errorCode, message: errorMessage });
        } else {
          error('[nuiteeBookingService] Authentication required for SSO', { code: errorCode, message: errorMessage });
        }
      } else if (status === 500) {
        if (errorCode === 'SSO_NOT_CONFIGURED') {
          error('[nuiteeBookingService] SSO not configured on backend', { code: errorCode, message: errorMessage });
        } else if (errorCode === 'SSO_GENERATION_FAILED') {
          error('[nuiteeBookingService] SSO generation failed', { code: errorCode, message: errorMessage });
        } else {
          error('[nuiteeBookingService] SSO request failed', { code: errorCode, message: errorMessage });
        }
      } else if (status === 503 || status === 504) {
        error('[nuiteeBookingService] SSO service unavailable or timeout', { code: errorCode, message: errorMessage, status });
      } else {
        error('[nuiteeBookingService] SSO request failed', { 
          code: errorCode || 'UNKNOWN_ERROR', 
          message: errorMessage,
          status,
        });
      }
      
      // Enhance error object with code for better error handling in components
      const enhancedError: BookingEnhancedError = new Error(errorMessage || 'SSO link generation failed');
      enhancedError.code = errorCode;
      enhancedError.status = status;
      enhancedError.responseData = axiosErr?.response?.data;
      throw enhancedError;
    }
  },

  /**
   * Get backend environment mode (sandbox or production)
   * Used to determine if sandbox banner should be shown in API booking flow
   * 
   * @returns Environment response with isSandbox flag
   */
  getEnvironment: async (): Promise<EnvironmentResponse> => {
    const endpoint = '/api/v1/booking/environment';
    
    try {
      log('[nuiteeBookingService] GET /api/v1/booking/environment - Request', {
        endpoint,
      });

      const response = await apiClient.get<EnvironmentResponse>(
        endpoint,
        { timeout: 5000 } // 5 seconds timeout
      );

      log('[nuiteeBookingService] GET /api/v1/booking/environment - Response', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        isSandbox: response.data.data?.isSandbox,
        environment: response.data.data?.environment,
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      const status = axiosErr?.response?.status;
      const errorMessage = axiosErr?.response?.data?.error || axiosErr?.message;
      
      error('[nuiteeBookingService] GET /api/v1/booking/environment - Error', {
        endpoint,
        error: {
          message: errorMessage,
          status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
        },
      });
      
      throw err;
    }
  },

  /**
   * Get hotel ID from Google Place ID
   * Checks if a hotel exists in Nuitee's inventory before searching for rates
   * No dates required - can be called immediately when user clicks "Book It Now"
   * 
   * @param placeId - Google Place ID
   * @returns Hotel ID and metadata if found, or null if not found
   * @throws Error with specific error codes for different failure scenarios
   */
  getHotelIdFromPlaceId: async (placeId: string): Promise<HotelIdLookupResponse> => {
    const endpoint = `/api/v1/booking/hotel-id?placeId=${encodeURIComponent(placeId)}`;
    
    try {
      log('[nuiteeBookingService] GET /api/v1/booking/hotel-id - Request', {
        endpoint,
        placeId,
      });

      // Use shorter timeout for hotel ID lookup (should be fast - typically < 1s)
      const response = await apiClient.get<HotelIdLookupResponse>(endpoint, {
        timeout: 5000, // 5 seconds timeout for hotel ID lookup
      });

      log('[nuiteeBookingService] GET /api/v1/booking/hotel-id - Response', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        responseData: {
          hotelId: response.data.data?.hotelId,
          hotelName: response.data.data?.hotelName,
          exactMatch: response.data.data?.exactMatch,
          placeId: response.data.data?.placeId,
          error: response.data.data?.error,
        },
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      const status = axiosErr?.response?.status;
      const errorCode = axiosErr?.response?.data?.code;
      const errorMessage = axiosErr?.response?.data?.error || axiosErr?.message;
      
      log('[nuiteeBookingService] GET /api/v1/booking/hotel-id - Error', {
        endpoint,
        error: {
          message: errorMessage,
          status,
          statusText: axiosErr?.response?.statusText,
          code: errorCode,
          responseData: axiosErr?.response?.data,
        },
      });
      
      // Handle specific error codes with appropriate error messages
      if (status === 400) {
        if (errorCode === 'MISSING_PLACE_ID' || errorCode === 'INVALID_PLACE_ID') {
          error('[nuiteeBookingService] Invalid Place ID', { code: errorCode, message: errorMessage });
        } else if (errorCode === 'INVALID_REQUEST') {
          error('[nuiteeBookingService] Invalid request to Nuitee API', { code: errorCode, message: errorMessage });
        } else {
          error('[nuiteeBookingService] Bad request', { code: errorCode, message: errorMessage });
        }
      } else if (status === 429) {
        // Rate limit exceeded
        error('[nuiteeBookingService] Rate limit exceeded', { code: errorCode, message: errorMessage });
      } else if (status === 503) {
        // Service unavailable
        error('[nuiteeBookingService] Service unavailable', { code: errorCode, message: errorMessage });
      } else if (status === 504) {
        // Gateway timeout
        error('[nuiteeBookingService] Request timeout', { code: errorCode, message: errorMessage });
      } else if (status === 500) {
        // Internal server error
        error('[nuiteeBookingService] Hotel ID lookup failed', { code: errorCode, message: errorMessage });
      } else {
        // Other errors
        error('[nuiteeBookingService] Hotel ID lookup failed', { 
          code: errorCode || 'UNKNOWN_ERROR', 
          message: errorMessage,
          status,
        });
      }
      
      // Enhance error object with code for better error handling in components
      const enhancedError: BookingEnhancedError = new Error(errorMessage || 'Hotel ID lookup failed');
      enhancedError.code = errorCode;
      enhancedError.status = status;
      enhancedError.responseData = axiosErr?.response?.data;
      throw enhancedError;
    }
  },

  /**
   * Semantic hotel search by natural-language query (discovery only; no rates).
   * GET /api/v1/booking/semantic-search. Backend enriches results with latitude/longitude when available.
   */
  searchHotelsBySemanticQuery: async (params: SemanticSearchParams): Promise<SemanticSearchResponse> => {
    const query = (params.query ?? '').trim();
    if (!query) {
    const err: BookingEnhancedError = new Error('query parameter is required');
    err.status = 400;
    throw err;
    }
    const endpoint = '/api/v1/booking/semantic-search';
    const requestParams: Record<string, string | number> = { query };
    if (params.limit != null) requestParams.limit = Math.min(50, Math.max(1, params.limit));
    else requestParams.limit = 25;
    if (params.min_rating != null) requestParams.min_rating = Math.min(5, Math.max(0, params.min_rating));

    try {
      log('[nuiteeBookingService] GET /api/v1/booking/semantic-search - Request', {
        endpoint,
        query,
        limit: requestParams.limit,
        min_rating: requestParams.min_rating,
      });
      const response = await apiClient.get<SemanticSearchResponse>(endpoint, {
        params: requestParams,
        timeout: 10000,
      });
      log('[nuiteeBookingService] GET /api/v1/booking/semantic-search - Response', {
        endpoint,
        status: response.status,
        dataLength: response.data?.data?.length ?? 0,
      });
      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      const status = axiosErr?.response?.status;
      const errorMessage = axiosErr?.response?.data?.error ?? axiosErr?.response?.data?.message ?? axiosErr?.message ?? 'Semantic search failed';
      log('[nuiteeBookingService] GET /api/v1/booking/semantic-search - Error', {
        endpoint,
        status,
        message: errorMessage,
      });
      error('[nuiteeBookingService] Semantic search failed', err);
      const enhancedError: BookingEnhancedError = new Error(errorMessage);
      enhancedError.status = status;
      enhancedError.responseData = axiosErr?.response?.data;
      throw enhancedError;
    }
  },

  /**
   * Search hotels by Place ID, coordinates, or specific hotel IDs
   * Transforms frontend params to backend format and transforms response back
   * Search priority: hotelIds > placeId > latitude/longitude
   */
  searchHotels: async (params: HotelSearchParams): Promise<HotelSearchResponse> => {
    const endpoint = '/api/v1/booking/search';
    
    log('[nuiteeBookingService] searchHotels called', {
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      checkInType: typeof params.checkIn,
      checkOutType: typeof params.checkOut,
      checkInFormat: /^\d{4}-\d{2}-\d{2}$/.test(params.checkIn) ? 'YYYY-MM-DD' : 'UNKNOWN',
      checkOutFormat: /^\d{4}-\d{2}-\d{2}$/.test(params.checkOut) ? 'YYYY-MM-DD' : 'UNKNOWN',
      hotelIds: params.hotelIds,
      placeId: params.placeId,
      adults: params.adults,
      rooms: params.rooms,
    });
    
    try {
      // Transform frontend params to backend format
      // Search priority: hotelIds > placeId > latitude/longitude
      const backendRequest: BackendSearchRequest = {
        checkin: params.checkIn, // lowercase
        checkout: params.checkOut, // lowercase
        occupancies: Array(params.rooms).fill(null).map(() => ({
          adults: params.adults,
          children: [], // No children for now
        })),
        maxRatesPerHotel: 20, // Get up to 20 rate options per hotel (different meal plans, cancellation policies, etc.)
      };

      // Add search parameters based on priority
      if (params.hotelIds && params.hotelIds.length > 0) {
        // Exact match search - use hotelIds (highest priority)
        backendRequest.hotelIds = params.hotelIds;
      } else if (params.placeId) {
        // Place ID proximity search
        backendRequest.placeId = params.placeId;
      } else if (params.latitude && params.longitude) {
        // Coordinate fallback
        backendRequest.latitude = params.latitude;
        backendRequest.longitude = params.longitude;
      }

      log('[nuiteeBookingService] POST /api/v1/booking/search - Request', {
        endpoint,
        requestBody: backendRequest,
        checkin: backendRequest.checkin,
        checkout: backendRequest.checkout,
        checkinType: typeof backendRequest.checkin,
        checkoutType: typeof backendRequest.checkout,
      });

      // Hotel search can be slow on live inventory — give the backend enough time to respond
      const response = await apiClient.post<BackendSearchResponse>(endpoint, backendRequest, { timeout: 30000 });

      log('[nuiteeBookingService] POST /api/v1/booking/search - Response received', {
        status: response.status,
        hasData: !!response.data,
        hotelsCount: response.data?.data?.hotels?.length || 0,
        requestCheckin: backendRequest.checkin,
        requestCheckout: backendRequest.checkout,
      });

      // Extract nested data structure
      // Response structure: { status: 'success', pricing: {...}, data: { hotels: [...], data: [...] } }
      const nuiteeData = response.data?.data || {};
      const hotelsMetadata = nuiteeData.hotels || [];
      const hotelsWithRates = nuiteeData.data || [];
      const pricingBlock = response.data?.pricing;

      log('[nuiteeBookingService] POST /api/v1/booking/search - Response (raw)', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        responseStatus: response.data?.status,
        responseDataStructure: {
          hasNestedData: !!response.data?.data,
          hotelsMetadataCount: hotelsMetadata.length,
          hotelsWithRatesCount: hotelsWithRates.length,
          hotelsMetadataIsArray: Array.isArray(hotelsMetadata),
          hotelsWithRatesIsArray: Array.isArray(hotelsWithRates),
        },
      });

      // Log detailed structure of hotels with rates
      if (Array.isArray(hotelsWithRates)) {
        hotelsWithRates.forEach((hotelData, index) => {
          log(`[nuiteeBookingService] Hotel ${index + 1} raw data`, {
            hotelId: hotelData.hotelId,
            roomTypesCount: hotelData.roomTypes?.length || 0,
            roomTypes: hotelData.roomTypes?.map((rt) => ({
              offerId: rt.offerId,
              roomTypeId: rt.roomTypeId,
              name: rt.rates?.[0]?.name,
              ratesCount: rt.rates?.length || 0,
              rates: rt.rates?.map((r) => ({
                rateId: r.rateId,
                mappedRoomId: r.mappedRoomId,
                name: r.name,
                boardName: r.boardName,
                price: r.retailRate?.total?.[0]?.amount,
                currency: r.retailRate?.total?.[0]?.currency,
              })),
            })),
          });
        });
      }

      // Transform backend response to frontend format
      // Merge data (rates) with hotels (metadata) by matching hotelId with hotels[].id
      const hotelsMap = new Map<string, Hotel>();
      
      // First, create hotel entries from metadata if available
      if (Array.isArray(hotelsMetadata)) {
        hotelsMetadata.forEach((hotelMeta) => {
          hotelsMap.set(hotelMeta.id, {
            hotelId: hotelMeta.id,
            name: hotelMeta.name,
            address: hotelMeta.address,
            city: hotelMeta.city,
            country: hotelMeta.country,
            rating: hotelMeta.rating,
            images: hotelMeta.main_photo ? [hotelMeta.main_photo] : [],
            latitude: hotelMeta.latitude,
            longitude: hotelMeta.longitude,
            google_place_id: hotelMeta.google_place_id ?? undefined,
            rooms: [],
            rates: [], // Keep for backward compatibility
          });
        });
      }

      // Then, add rate data from data array
      if (Array.isArray(hotelsWithRates)) {
        hotelsWithRates.forEach((hotelData) => {
          const hotel = hotelsMap.get(hotelData.hotelId) || {
            hotelId: hotelData.hotelId,
            name: 'Unknown Hotel',
            address: '',
            rooms: [],
            rates: [], // Keep for backward compatibility
          };

          // Process each roomType and group rates by mappedRoomId
          const roomsMap = new Map<number, HotelRoom>();

          if (Array.isArray(hotelData.roomTypes)) {
            hotelData.roomTypes.forEach((roomType, roomTypeIdx) => {
              if (!Array.isArray(roomType.rates)) return;

              // Log room type photos for debugging (first room type only)
              if (roomTypeIdx === 0) {
                log('[nuiteeBookingService] RoomType photos debug', {
                  hotelId: hotelData.hotelId,
                  roomTypeId: roomType.roomTypeId,
                  offerId: roomType.offerId,
                  hasPhotos: !!roomType.photos,
                  photosLength: roomType.photos?.length || 0,
                  photosData: roomType.photos?.slice(0, 2),
                  roomTypeKeys: Object.keys(roomType),
                });
              }

              // Group rates by mappedRoomId
              roomType.rates.forEach((rate) => {
                const mappedRoomId = rate.mappedRoomId || 0;
                
                // Create or get room group
                if (!roomsMap.has(mappedRoomId)) {
                  roomsMap.set(mappedRoomId, {
                    mappedRoomId,
                    roomName: rate.name || 'Room',
                    roomTypeId: roomType.roomTypeId,
                    offerId: roomType.offerId, // offerId is at roomType level
                    rates: [],
                    roomImages: roomType.photos?.map((p) => p.url) || [],
                  });
                }

                const room = roomsMap.get(mappedRoomId)!;
                const totalAmount = rate.retailRate?.total?.[0];
                const suggestedAmount = rate.retailRate?.suggestedSellingPrice?.[0];
                const initialPriceAmount = rate.retailRate?.initialPrice?.[0]?.amount;
                
                // Log pricing data for discount debugging
                if (totalAmount) {
                  const displayPrice = suggestedAmount?.amount || totalAmount.amount;
                  const hasDiscount = initialPriceAmount && initialPriceAmount > displayPrice;
                  log('[nuiteeBookingService] Rate pricing debug', {
                    roomName: rate.name,
                    totalPrice: totalAmount.amount,
                    displayPrice,
                    initialPrice: initialPriceAmount,
                    hasDiscount,
                    discountPercent: hasDiscount ? Math.round(((initialPriceAmount - displayPrice) / initialPriceAmount) * 100) : 0,
                  });
                }
                
                if (totalAmount) {
                  // Determine if breakfast is included based on boardType
                  // BB = Bed & Breakfast, HB = Half Board, FB = Full Board, AI = All Inclusive
                  const boardType = rate.boardType?.toUpperCase() || '';
                  const breakfastIncluded = ['BB', 'HB', 'FB', 'AI'].includes(boardType) || 
                    (rate.boardName?.toLowerCase().includes('breakfast'));
                  
                  const hotelRate: HotelRate = {
                    rateId: rate.rateId || '',
                    offerId: roomType.offerId, // Use this for prebook when user selects this rate (each rate can have a different offerId)
                    mappedRoomId,
                    roomName: rate.name || 'Room',
                    boardName: rate.boardName || 'Room Only',
                    boardType: rate.boardType,
                    price: totalAmount.amount,
                    displayPrice: suggestedAmount?.amount || totalAmount.amount,
                    initialPrice: initialPriceAmount, // Original price for discount calculation
                    currency: totalAmount.currency,
                    roomImages: roomType.photos?.map((p) => p.url) || [],
                    cancellationPolicy: rate.cancellationPolicies?.refundableTag,
                    isRefundable: rate.cancellationPolicies?.refundableTag === 'RFN',
                    cancelPolicyInfos: rate.cancellationPolicies?.cancelPolicyInfos || [],
                    breakfastIncluded,
                    taxesAndFees: rate.retailRate?.taxesAndFees?.map((tf) => ({
                      amount: tf.amount || 0,
                      currency: tf.currency || totalAmount.currency,
                      included: tf.included,
                    })),
                    paymentTypes: rate.paymentTypes || [],
                    perks: rate.perks || [],
                    adultCount: rate.adultCount,
                    childCount: rate.childCount,
                  };
                  
                  room.rates.push(hotelRate);
                }
              });
            });
          }

          // Sort rates by price (discounted) within each room (so cheapest actual cost is first)
          roomsMap.forEach((room) => {
            room.rates.sort((a, b) => a.price - b.price);
          });

          // Convert map to array and sort rooms by cheapest discounted rate
          const roomsArray = Array.from(roomsMap.values());
          roomsArray.sort((a, b) => {
            const priceA = a.rates[0]?.price || Infinity;
            const priceB = b.rates[0]?.price || Infinity;
            return priceA - priceB;
          });

          hotel.rooms = roomsArray;
          
          // Also create flat rates array for backward compatibility
          hotel.rates = roomsArray.flatMap((room) => room.rates);

          // Log processed hotel structure
          log(`[nuiteeBookingService] Processed hotel: ${hotel.name}`, {
            hotelId: hotel.hotelId,
            roomsCount: hotel.rooms.length,
            totalRatesCount: hotel.rates.length,
            rooms: hotel.rooms.map((room) => ({
              mappedRoomId: room.mappedRoomId,
              roomName: room.roomName,
              offerId: room.offerId,
              ratesCount: room.rates.length,
              rates: room.rates.map((rate) => ({
                rateId: rate.rateId,
                boardName: rate.boardName,
                price: rate.price,
                displayPrice: rate.displayPrice,
                currency: rate.currency,
                isRefundable: rate.isRefundable,
              })),
            })),
          });

          hotelsMap.set(hotelData.hotelId, hotel);
        });
      }

      const hotels = Array.from(hotelsMap.values());

      log('[nuiteeBookingService] POST /api/v1/booking/search - Response (transformed)', {
        endpoint,
        hotelsCount: hotels.length,
        hotels: hotels.map(hotel => ({
          hotelId: hotel.hotelId,
          name: hotel.name,
          address: hotel.address,
          rating: hotel.rating,
          ratesCount: hotel.rates?.length || 0,
        })),
      });

      return {
        data: {
          hotels,
        },
        pricing: pricingBlock,
      };
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] POST /api/v1/booking/search - Error', {
        endpoint,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
        },
      });
      error('[nuiteeBookingService] Hotel search failed', err);
      throw err;
    }
  },

  /**
   * Prebook a hotel rate
   * Only offerId is required - dates, adults, rooms are already encoded in the offer
   */
  prebookRate: async (params: PrebookParams): Promise<PrebookResponse> => {
    const endpoint = '/api/v1/booking/prebook';
    
    try {
      log('[nuiteeBookingService] POST /api/v1/booking/prebook - Request', {
        endpoint,
        requestBody: {
          offerId: params.offerId,
        },
      });

      // Backend waits 45s + retry (2s + 45s) ≈ 92s max; client must not timeout before that
      const response = await apiClient.post<PrebookResponse>(
        endpoint,
        { offerId: params.offerId },
        { timeout: 95000 } // 95 seconds for prebook (backend may retry once)
      );

      log('[nuiteeBookingService] POST /api/v1/booking/prebook - Response', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        responseData: {
          prebookId: response.data.data?.prebookId,
          transactionId: response.data.data?.transactionId,
          hasSecretKey: !!response.data.data?.secretKey,
          sandbox: response.data.sandbox,
          hotelId: response.data.data?.hotelId,
          price: response.data.data?.price,
          currency: response.data.data?.currency,
        },
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] POST /api/v1/booking/prebook - Error', {
        endpoint,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
        },
      });
      error('[nuiteeBookingService] Prebook failed', err);
      throw err;
    }
  },

  /**
   * Complete booking after payment
   */
  completeBooking: async (params: CompleteBookingParams): Promise<CompleteBookingResponse> => {
    const endpoint = '/api/v1/booking/book';
    
    try {
      // Build clientReference with format: gt:{postId|NoPost}:hotel:{hotelId}:place:{googlePlaceId}:ts:{timestamp}
      // Same format as whitelabel for full traceability
      let clientReference: string | undefined;
      if (params.hotelId) {
        const isValidPostId = params.postId && typeof params.postId === 'string' && /^[0-9a-fA-F]{24}$/.test(params.postId);
        const postPart = isValidPostId ? params.postId : 'NoPost';
        const placePart = params.googlePlaceId || 'NoPlace';
        const timestamp = Date.now();
        clientReference = `gt:${postPart}:hotel:${params.hotelId}:place:${placePart}:ts:${timestamp}`;
        
        log('[nuiteeBookingService] Built clientReference for API booking', {
          postId: params.postId || null,
          hotelId: params.hotelId,
          googlePlaceId: params.googlePlaceId || null,
          timestamp,
          clientReference,
        });
      }
      
      const requestBody: {
        prebookId: string;
        transactionId: string;
        holder: CompleteBookingParams['holder'];
        guests: CompleteBookingParams['guests'];
        clientReference?: string;
        referral_creator_id?: string;
        location_id?: string;
        post_location_id?: string;
      } = {
        prebookId: params.prebookId,
        transactionId: params.transactionId,
        holder: params.holder,
        guests: params.guests,
        ...(clientReference && { clientReference }),
        ...(params.referralCreatorId && { referral_creator_id: params.referralCreatorId }),
        ...(params.locationId && { location_id: params.locationId }),
        ...(params.postLocationId && { post_location_id: params.postLocationId }),
      };
      
      log('[nuiteeBookingService] POST /api/v1/booking/book - Request', {
        endpoint,
        requestBody: {
          prebookId: params.prebookId,
          transactionId: params.transactionId,
          holder: {
            firstName: params.holder.firstName,
            lastName: params.holder.lastName,
            email: params.holder.email,
          },
          guestsCount: params.guests.length,
          hasClientReference: !!clientReference,
          clientReference: clientReference || 'omitted',
        },
      });

      // Increase timeout to 30s - Nuitee booking API can be slow
      const response = await apiClient.post<CompleteBookingResponse>(endpoint, requestBody, { timeout: 30000 });

      // Log full response structure for debugging
      log('[nuiteeBookingService] POST /api/v1/booking/book - Response', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        responseStructure: {
          hasStatus: 'status' in response.data,
          hasData: 'data' in response.data,
          dataType: typeof response.data?.data,
          topLevelKeys: Object.keys(response.data || {}),
          dataKeys: response.data?.data ? Object.keys(response.data.data) : [],
        },
        rawResponse: response.data,
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] POST /api/v1/booking/book - Error', {
        endpoint,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
        },
      });
      error('[nuiteeBookingService] Booking completion failed', err);
      throw err;
    }
  },

  /**
   * Get hotel details
   * @param hotelId - Hotel ID (e.g., 'lp1efc1')
   * @param language - Language code (default: 'en')
   */
  getHotelDetails: async (hotelId: string, language: string = 'en') => {
    const endpoint = `/api/v1/booking/hotel/${hotelId}`;
    
    try {
      log('[nuiteeBookingService] GET /api/v1/booking/hotel/:hotelId - Request', {
        endpoint,
        hotelId,
        language,
      });

      const response = await apiClient.get(endpoint, {
        params: { language },
        // Don't throw for 400 — some hotel IDs exist in inventory but lack a details record
        validateStatus: (status) => status === 200 || status === 400,
      });

      log('[nuiteeBookingService] GET /api/v1/booking/hotel/:hotelId - Response', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        responseData: response.data,
      });

      if (response.status === 400) {
        log('[nuiteeBookingService] Hotel details not available for this hotel ID', { hotelId });
        return { status: 'not_found', data: null };
      }

      // Log hotel facilities to debug display order
      if (response.data?.data?.hotelFacilities) {
        const facilities = response.data.data.hotelFacilities;
        log('[nuiteeBookingService] Hotel facilities from API', {
          hotelId,
          facilitiesCount: facilities.length,
          first8Facilities: facilities.slice(0, 8),
          facilities9to16: facilities.slice(8, 16),
          allFacilitiesString: JSON.stringify(facilities),
        });
      }

      // Log rooms structure specifically
      if (response.data?.data?.rooms) {
        log('[nuiteeBookingService] Hotel details rooms structure', {
          hotelId,
          roomsCount: response.data.data.rooms.length,
          roomsStructure: response.data.data.rooms.slice(0, 2).map((r: Record<string, unknown>) => ({
            id: r.id,
            roomName: r.roomName,
            hasPhotos: !!r.photos,
            photosType: typeof r.photos,
            photosIsArray: Array.isArray(r.photos),
            photosCount: Array.isArray(r.photos) ? r.photos.length : 0,
            photosData: r.photos,
            allKeys: Object.keys(r),
          })),
        });
      } else {
        log('[nuiteeBookingService] Hotel details - no rooms found', {
          hotelId,
          hasData: !!response.data?.data,
          dataKeys: response.data?.data ? Object.keys(response.data.data) : [],
          responseStructure: JSON.stringify(response.data, null, 2).substring(0, 500),
        });
      }

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] GET /api/v1/booking/hotel/:hotelId - Error', {
        endpoint,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
        },
      });
      error('[nuiteeBookingService] Failed to fetch hotel details', err);
      throw err;
    }
  },

  /**
   * Get booking details
   */
  getBookingDetails: async (bookingId: string) => {
    const endpoint = `/api/v1/booking/${bookingId}`;
    
    try {
      log('[nuiteeBookingService] GET /api/v1/booking/:bookingId - Request', {
        endpoint,
        bookingId,
      });

      const response = await apiClient.get(endpoint);

      log('[nuiteeBookingService] GET /api/v1/booking/:bookingId - Response', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        responseData: response.data,
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] GET /api/v1/booking/:bookingId - Error', {
        endpoint,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
        },
      });
      error('[nuiteeBookingService] Failed to fetch booking details', err);
      throw err;
    }
  },

  /**
   * Get hotel reviews with pagination
   */
  getHotelReviews: async (
    hotelId: string,
    options?: { page?: number; limit?: number; sort?: 'newest' | 'highest' | 'lowest'; getSentiment?: boolean }
  ): Promise<HotelReviewsResponse> => {
    const { page = 1, limit = 10, sort = 'newest', getSentiment = false } = options || {};
    const endpoint = `/api/v1/booking/hotel/${hotelId}/reviews`;
    
    // Backend expects getSentiment as string 'true' or 'false', not boolean
    const params: Record<string, string | number> = {
      page,
      limit: Math.min(50, Math.max(1, limit)), // Enforce max limit of 50
      sort,
      getSentiment: getSentiment ? 'true' : 'false',
    };
    
    try {
      log('[nuiteeBookingService] GET /api/v1/booking/hotel/:hotelId/reviews - Request', {
        endpoint,
        hotelId,
        params,
        fullUrl: `${apiClient.defaults.baseURL}${endpoint}`,
      });

      const response = await apiClient.get<HotelReviewsResponse>(endpoint, {
        params,
      });

      log('[nuiteeBookingService] GET /api/v1/booking/hotel/:hotelId/reviews - Response', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        hasData: !!response.data,
        responseStatus: response.data?.status,
        reviewsCount: response.data?.data?.reviews?.length || 0,
        hasSummary: !!response.data?.data?.summary,
        overallScore: response.data?.data?.summary?.overallScore,
        reviewCount: response.data?.data?.summary?.reviewCount,
        hasSentiment: !!response.data?.data?.summary?.sentiment,
        hasPagination: !!response.data?.data?.pagination,
        hasMore: response.data?.data?.pagination?.hasMore,
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] GET /api/v1/booking/hotel/:hotelId/reviews - Error', {
        endpoint,
        hotelId,
        page,
        limit,
        sort,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
          errorType: axiosErr?.constructor?.name,
          stack: axiosErr?.stack,
        },
        fullError: err,
      });
      error('[nuiteeBookingService] Failed to fetch hotel reviews', err);
      throw err;
    }
  },

  /**
   * Get facilities taxonomy (cacheable for 7 days)
   */
  getFacilities: async (language: string = 'en'): Promise<FacilitiesResponse> => {
    const endpoint = '/api/v1/booking/facilities';
    
    try {
      log('[nuiteeBookingService] GET /api/v1/booking/facilities - Request', {
        endpoint,
        language,
      });

      const response = await apiClient.get<FacilitiesResponse>(endpoint, {
        params: { language },
      });

      log('[nuiteeBookingService] GET /api/v1/booking/facilities - Response', {
        endpoint,
        status: response.status,
        facilitiesCount: response.data.data?.facilities?.length || 0,
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] GET /api/v1/booking/facilities - Error', {
        endpoint,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
        },
      });
      error('[nuiteeBookingService] Failed to fetch facilities', err);
      throw err;
    }
  },

  /**
   * List user's bookings with pagination
   * Efficient for getting count: use limit=1 and read pagination.totalCount
   */
  getUserBookings: async (options?: {
    status?: 'confirmed' | 'pending' | 'cancelled';
    limit?: number;
    page?: number;
  }): Promise<BookingsListResponse> => {
    const endpoint = '/api/v1/booking';
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.page) params.append('page', String(options.page));

    const url = params.toString() ? `${endpoint}?${params}` : endpoint;

    try {
      log('[nuiteeBookingService] GET /api/v1/booking - Request', {
        endpoint: url,
        options,
      });

      const response = await apiClient.get<BookingsListResponse>(url);

      log('[nuiteeBookingService] GET /api/v1/booking - Response', {
        endpoint: url,
        status: response.status,
        bookingCount: response.data.data.bookings.length,
        pagination: response.data.data.pagination,
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] GET /api/v1/booking - Error', {
        endpoint: url,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
          code: axiosErr?.response?.data?.code,
        },
      });
      error('[nuiteeBookingService] Failed to fetch user bookings', err);
      throw err;
    }
  },

  /**
   * Cancel a booking
   */
  cancelBooking: async (bookingId: string, reason?: string): Promise<CancelBookingResponse> => {
    const endpoint = `/api/v1/booking/${bookingId}/cancel`;

    try {
      log('[nuiteeBookingService] PUT /api/v1/booking/:bookingId/cancel - Request', {
        endpoint,
        bookingId,
        reason,
      });

      const response = await apiClient.put<CancelBookingResponse>(endpoint, { reason });

      log('[nuiteeBookingService] PUT /api/v1/booking/:bookingId/cancel - Response', {
        endpoint,
        status: response.status,
        responseData: response.data,
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] PUT /api/v1/booking/:bookingId/cancel - Error', {
        endpoint,
        bookingId,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
          code: axiosErr?.response?.data?.code,
        },
      });
      error('[nuiteeBookingService] Failed to cancel booking', err);
      throw err;
    }
  },

  /**
   * Amend guest name(s) on a booking
   * Note: Nuitee only supports name changes, not room/date modifications
   */
  amendBooking: async (
    bookingId: string,
    guestData: { firstName: string; lastName: string } | { guests: Array<{ occupancyNumber: number; firstName: string; lastName: string }> }
  ): Promise<AmendBookingResponse> => {
    const endpoint = `/api/v1/booking/${bookingId}/amend`;

    try {
      log('[nuiteeBookingService] PUT /api/v1/booking/:bookingId/amend - Request', {
        endpoint,
        bookingId,
        guestData,
      });

      const response = await apiClient.put<AmendBookingResponse>(endpoint, guestData);

      log('[nuiteeBookingService] PUT /api/v1/booking/:bookingId/amend - Response', {
        endpoint,
        status: response.status,
        responseData: response.data,
      });

      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;
      log('[nuiteeBookingService] PUT /api/v1/booking/:bookingId/amend - Error', {
        endpoint,
        bookingId,
        error: {
          message: axiosErr?.message,
          status: axiosErr?.response?.status,
          statusText: axiosErr?.response?.statusText,
          responseData: axiosErr?.response?.data,
          code: axiosErr?.response?.data?.code,
        },
      });
      error('[nuiteeBookingService] Failed to amend booking', err);
      throw err;
    }
  },

  /**
   * Fetch both Google Place IDs (city-level + hotel-specific) for a Nuitee hotel on demand.
   * Results are cached in memory for the session lifetime so the same hotel is never re-fetched.
   * GET /api/v1/booking/hotel/:hotelId/google-place-ids
   */
  getHotelPlaceIds: async (hotelId: string): Promise<HotelPlaceIdsResponse> => {
    const cached = placeIdCache.get(hotelId);
    if (cached) {
      debug('[nuiteeBookingService] getHotelPlaceIds - cache hit', { hotelId });
      return { nuitee_hotel_id: hotelId, ...cached };
    }

    const endpoint = `/api/v1/booking/hotel/${hotelId}/google-place-ids`;
    debug('[nuiteeBookingService] getHotelPlaceIds - fetching', { endpoint });

    const response = await apiClient.get<HotelPlaceIdsResponse>(endpoint, {
      timeout: 6000,
      validateStatus: (status) => status === 200 || status === 404,
    });

    const result: HotelPlaceIdsResponse = response.status === 200
      ? response.data
      : { nuitee_hotel_id: hotelId, google_place_id: null, hotel_google_place_id: null };

    placeIdCache.set(hotelId, {
      google_place_id: result.google_place_id,
      hotel_google_place_id: result.hotel_google_place_id,
    });

    debug('[nuiteeBookingService] getHotelPlaceIds - result', { hotelId, result });
    return result;
  },
};
